import {
  CONTENT_TYPES,
  FILE_TYPES,
} from "pages/student/activity/common/constants"
import { GAMES } from "pages/games/common/constants"

const normalizeRawList = (rawList) => {
  const list = Array.isArray(rawList)
    ? rawList
    : rawList?.gameSessionContents || rawList?.contents || []
  return Array.isArray(list) ? list : []
}

const extractFiles = (item) => {
  const files = item?.files || []
  const audioURL =
    files.find((f) => Number(f.fileTypeId) === FILE_TYPES.AUDIO)?.fileURL || ""
  const imageURL =
    files.find((f) => Number(f.fileTypeId) === FILE_TYPES.IMAGE)?.fileURL || ""
  return { audioURL, imageURL }
}

const findExplanation = (list, sequence, exerciseId) =>
  list.find(
    (item) =>
      Number(item.contentTypeId) === CONTENT_TYPES.EXPLANATION &&
      Number(item.sequence) === Number(sequence) &&
      item.exerciseId === exerciseId
  )

const findInstruction = (list, sequence, exerciseId) =>
  list.find(
    (item) =>
      Number(item.contentTypeId) === 13 &&
      Number(item.sequence) === Number(sequence) &&
      item.exerciseId === exerciseId
  )

const getQuestionMeta = (q) => ({
  sequence: q.sequence,
  contentId: q.contentId,
  exerciseId: q.exerciseId,
  gameSessionContentId: q.gameSessionContentId,
})

const buildBaseQuestion = (q, qIndex, extra = {}) => ({
  id: q.sequence || qIndex + 1,
  question: (q.text || "").trim(),
  contentId: q.contentId,
  sequence: q.sequence,
  gameSessionContentId: q.gameSessionContentId,
  exerciseId: q.exerciseId,
  moduleActivityId: q?.moduleActivityId || 0,
  ...extra,
})

const shuffleArray = (arr) => {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

const processFixItRushData = (rawList) => {
  const list = normalizeRawList(rawList)
  if (list.length === 0) return []

  const questions = list.filter(
    (item) =>
      item.contentTypeId === CONTENT_TYPES.QUESTION && item.isPrimaryContent
  )

  return questions.map((q, qIndex) => {
    const meta = getQuestionMeta(q)
    const { audioURL } = extractFiles(q)

    const optionsForQuestion = list.filter(
      (item) =>
        (item.contentTypeId === CONTENT_TYPES.WORD ||
          item.contentTypeId === CONTENT_TYPES.ANSWER) &&
        item.contentReference === meta.contentId &&
        item.exerciseId === meta.exerciseId
    )

    const options = optionsForQuestion.map((opt, optIndex) => ({
      id: String.fromCharCode(65 + optIndex),
      text: (opt.text || "").trim(),
      isAnswer: opt.contentTypeId === CONTENT_TYPES.ANSWER || opt.isAnswer,
      contentId: opt.contentId,
      gameSessionContentId: opt.gameSessionContentId,
      sequence: opt.sequence,
    }))

    const correctOpt = optionsForQuestion.find(
      (opt) => opt.contentTypeId === CONTENT_TYPES.ANSWER || opt.isAnswer
    )
    const correctSentence = correctOpt ? (correctOpt.text || "").trim() : ""

    const explanationItem = findExplanation(
      list,
      meta.sequence,
      meta.exerciseId
    )
    const explanationText = explanationItem ? explanationItem.text || "" : ""

    const instructionItem = findInstruction(
      list,
      meta.sequence,
      meta.exerciseId
    )
    const instructionText = instructionItem
      ? (instructionItem.text || "").trim()
      : ""

    return buildBaseQuestion(q, qIndex, {
      correctSentence,
      explanation: explanationText,
      options: shuffleArray(options),
      audioURL,
      instruction: instructionText,
    })
  })
}

const processSentenceScrambleData = (rawList) => {
  const list = normalizeRawList(rawList)
  if (list.length === 0) return []

  const questions = list.filter(
    (item) =>
      Number(item.contentTypeId) === 13 &&
      (item.isPrimaryContent === true ||
        String(item.isPrimaryContent).toLowerCase() === "true")
  )

  if (questions.length === 0) return []

  return questions.map((q, qIndex) => {
    const meta = getQuestionMeta(q)
    const { audioURL } = extractFiles(q)

    const wordsForQuestion = list
      .filter(
        (item) =>
          Number(item.contentTypeId) === 14 &&
          item.exerciseId === meta.exerciseId
      )
      .sort((a, b) => Number(a.sequence) - Number(b.sequence))

    const correctSentence = wordsForQuestion
      .map((w) => (w.text || "").trim())
      .join(" ")

    const wordObjects = wordsForQuestion.map((w, i) => ({
      word: (w.text || "").trim(),
      sequence: w.sequence,
      contentId: w.contentId,
      gameSessionContentId: w.gameSessionContentId,
      isLastWord: i === wordsForQuestion.length - 1,
      exerciseId: w.exerciseId,
      moduleActivityId: w?.moduleActivityId || 0,
    }))

    const scrambledWords = shuffleArray(wordObjects)

    return buildBaseQuestion(q, qIndex, {
      correctSentence,
      wordObjects,
      scrambledWords,
      audioURL,
    })
  })
}

const processScenarioSnapExercises = (rawList) => {
  const list = normalizeRawList(rawList)
  if (list.length === 0) return []

  const questions = list.filter((item) => item.isPrimaryContent === true)
  const allOptions = list.filter(
    (item) => item.contentTypeId === CONTENT_TYPES.RELATE_B
  )

  return questions.map((q, qIndex) => {
    const meta = getQuestionMeta(q)

    const correctOpt = allOptions.find(
      (opt) =>
        opt.sequence === meta.sequence && opt.exerciseId === meta.exerciseId
    )
    const incorrectOpts = allOptions.filter(
      (opt) => opt.sequence !== meta.sequence
    )
    const selectedIncorrect = shuffleArray(incorrectOpts).slice(0, 3)
    const combined = correctOpt
      ? [correctOpt, ...selectedIncorrect]
      : selectedIncorrect

    const options = combined.map((opt, optIndex) => ({
      id: String.fromCharCode(65 + optIndex),
      text: opt.text || "".trim(),
      isAnswer:
        opt.sequence === meta.sequence && opt.exerciseId === meta.exerciseId,
      contentId: opt.contentId,
      gameSessionContentId: opt.gameSessionContentId,
      sequence: opt.sequence,
    }))
    const shuffledOptions = shuffleArray(options)
    const correctSentence = correctOpt ? (correctOpt.text || "").trim() : ""

    const explanationItem = findExplanation(
      list,
      meta.sequence,
      meta.exerciseId
    )
    const explanationText = explanationItem ? explanationItem.text || "" : ""

    return buildBaseQuestion(q, qIndex, {
      correctSentence,
      explanation: explanationText,
      options: shuffledOptions,
    })
  })
}

const processMemoryPalaceData = (rawList) => {
  const list = normalizeRawList(rawList)
  if (list.length === 0) return []

  const primaryContentItems = list.filter(
    (item) => item.isPrimaryContent === true
  )

  const words = primaryContentItems.map((item) => {
    const meta = getQuestionMeta(item)
    const { audioURL } = extractFiles(item)

    const explanationItem = findExplanation(
      list,
      meta.sequence,
      meta.exerciseId
    )
    const imageURL = explanationItem?.files?.find(
      (f) => f.fileTypeId === FILE_TYPES.IMAGE
    )?.fileURL

    return {
      word: (item.text || "").trim(),
      translation: (item.translatedText || "").trim(),
      audioURL,
      imageURL,
      contentId: meta.contentId,
      sequence: meta.sequence,
      gameSessionContentId: meta.gameSessionContentId,
      exerciseId: meta.exerciseId,
      moduleActivityId: item?.moduleActivityId || 0,
    }
  })

  const limitedWords = words.slice(0, 9)
  if (limitedWords.length === 0) return []

  return [
    {
      id: 1,
      gameSessionContentId: limitedWords[0]?.gameSessionContentId || 0,
      words: limitedWords,
    },
  ]
}

const processConversationLabExercises = (rawList) => {
  const list = normalizeRawList(rawList)
  if (list.length === 0) return []

  const questions = list.filter(
    (item) =>
      item.isPrimaryContent === true ||
      String(item.isPrimaryContent).toLowerCase() === "true"
  )

  const allOptions = list.filter((item) => Number(item.contentTypeId) === 8)

  return questions.map((q, qIndex) => {
    const meta = getQuestionMeta(q)
    const { audioURL } = extractFiles(q)

    const correctOpt = allOptions.find(
      (opt) =>
        Number(opt.sequence) === Number(meta.sequence) &&
        opt.exerciseId === meta.exerciseId
    )
    const correctSentence = correctOpt ? (correctOpt.text || "").trim() : ""

    const instructionItem = findInstruction(
      list,
      meta.sequence,
      meta.exerciseId
    )
    const instructionText = instructionItem ? instructionItem.text || "" : ""
    const instructionImage = instructionItem?.files?.find(
      (f) => Number(f.fileTypeId) === FILE_TYPES.IMAGE
    )?.fileURL

    return buildBaseQuestion(q, qIndex, {
      instrucction: instructionText,
      questionTranslation: (q.translatedText || "").trim(),
      correctSentence,
      audioURL,
      imageURL: instructionImage,
      answer: correctSentence,
      answerTranslation: (correctOpt?.translatedText || "").trim(),
      audioAnswerURL: correctOpt?.files?.find(
        (f) => f.fileTypeId === FILE_TYPES.AUDIO
      )?.fileURL,
      answerImageURL: correctOpt?.files?.find(
        (f) => f.fileTypeId === FILE_TYPES.IMAGE
      )?.fileURL,
    })
  })
}

const processTranslationDuelExercises = (rawList) => {
  const list = normalizeRawList(rawList)
  if (list.length === 0) return []

  const questions = list.filter(
    (item) =>
      (item.isPrimaryContent === true ||
        String(item.isPrimaryContent).toLowerCase() === "true") &&
      Number(item.contentTypeId) === 1
  )

  const allQuestions = list.filter(
    (item) => Number(item.contentTypeId) === CONTENT_TYPES.WORD
  )

  const allOptions = list.filter(
    (item) => Number(item.contentTypeId) === CONTENT_TYPES.EXPLANATION
  )

  return questions.map((q, qIndex) => {
    const meta = getQuestionMeta(q)

    const explanationItem = findExplanation(
      list,
      meta.sequence,
      meta.exerciseId
    )

    const correctOpt = {
      text: (explanationItem.translatedText || "").trim(),
      isAnswer: true,
      contentId: explanationItem.contentId,
      gameSessionContentId: explanationItem.gameSessionContentId,
      sequence: explanationItem.sequence,
      originalText: q.text || "",
    }

    const otherOptions = allOptions.filter(
      (item) => Number(item.sequence) !== Number(meta.sequence)
    )
    const shuffledOthers = shuffleArray(otherOptions)
    const selectedIncorrect = shuffledOthers.slice(0, 3).map((other) => {
      const origen = allQuestions.find(
        (item) => item.sequence === other.sequence
      )
      return {
        text: (other.translatedText || "").trim(),
        originalText: origen?.text || "",
        isAnswer: false,
        contentId: Number(other.contentId),
        gameSessionContentId: other.gameSessionContentId,
        sequence: Number(other.sequence),
      }
    })

    const combined = [correctOpt, ...selectedIncorrect]
    const options = combined.map((opt) => ({
      id: opt.contentId,
      text: opt.text,
      isAnswer: opt.isAnswer,
      contentId: opt.contentId,
      gameSessionContentId: opt.gameSessionContentId,
      sequence: opt.sequence,
      originalText: opt.originalText,
    }))
    const shuffledOptions = shuffleArray(options)

    return buildBaseQuestion(q, qIndex, {
      correctSentence: correctOpt.text,
      explanation: "",
      options: shuffledOptions,
    })
  })
}

const processPhraseBuilderData = (rawList) => {
  const list = normalizeRawList(rawList)
  if (list.length === 0) return []

  const type1Items = list.filter((item) => Number(item.contentTypeId) === 1)
  const hasType1Partner = (exerciseId) => {
    if (exerciseId === undefined || exerciseId === null) return false
    return type1Items.some(
      (item) => Number(item.exerciseId) === Number(exerciseId)
    )
  }

  const questions = list.filter((item) => {
    const cId = Number(item.contentTypeId)
    if (cId === 3) return true
    if (cId === 9) return hasType1Partner(item.exerciseId)
    return false
  })

  const explanations = list.filter((item) => {
    const cId = Number(item.contentTypeId)
    if (cId === 9) return !hasType1Partner(item.exerciseId)
    return false
  })

  return questions.map((q, qIndex) => {
    const meta = getQuestionMeta(q)
    const qFiles = extractFiles(q)
    let aGameSessionContentId = 0

    let optionsForQuestion = []
    const qCId = q.contentTypeId
    if (qCId === CONTENT_TYPES.QUESTION) {
      optionsForQuestion = list.filter(
        (item) =>
          item.contentTypeId === CONTENT_TYPES.ANSWER &&
          item.exerciseId === meta.exerciseId
      )
    }
    if (qCId === CONTENT_TYPES.TEXT) {
      optionsForQuestion = list.filter(
        (item) =>
          item.contentTypeId === CONTENT_TYPES.WORD &&
          item.exerciseId === meta.exerciseId
      )
      if (optionsForQuestion.length > 0) {
        aGameSessionContentId = optionsForQuestion?.[0]?.gameSessionContentId
      }
    }

    const options = optionsForQuestion.map((opt, optIndex) => {
      const optFiles = extractFiles(opt)

      let isAnswer = false
      if (qCId === CONTENT_TYPES.QUESTION) {
        isAnswer = opt.isAnswer === true
      } else {
        isAnswer = opt.contentTypeId === CONTENT_TYPES.WORD
      }

      return {
        id: String.fromCharCode(65 + optIndex),
        text: (opt.text || "").trim(),
        isAnswer,
        contentId: opt.contentId,
        gameSessionContentId: opt.gameSessionContentId,
        sequence: opt.sequence,
        audioURL: optFiles.audioURL,
        imageURL: optFiles.imageURL,
      }
    })

    const correctOpt = options.find((opt) => opt.isAnswer)
    const correctSentence = correctOpt ? correctOpt.text : ""

    let explanationText = ""
    let explanationAudioURL = ""
    let explanationImageURL = ""

    if (explanations.length > 0) {
      const qOrigIndex = list.indexOf(q)
      let closestExp = null
      let minDistance = Infinity

      for (const exp of explanations) {
        const expOrigIndex = list.indexOf(exp)
        const distance = Math.abs(qOrigIndex - expOrigIndex)
        if (distance < minDistance) {
          minDistance = distance
          closestExp = exp
        }
      }

      if (closestExp) {
        explanationText = (closestExp.text || "").trim()
        const expFiles = extractFiles(closestExp)
        explanationAudioURL = expFiles.audioURL
        explanationImageURL = expFiles.imageURL
      }
    }

    return {
      id: meta.sequence || qIndex + 1,
      question: (q.text || "").trim(),
      correctSentence,
      explanation:
        explanationText?.trim()?.length === 0 &&
        explanationAudioURL?.trim()?.length === 0 &&
        explanationImageURL?.trim()?.length === 0
          ? null
          : {
              text: explanationText,
              audioURL: explanationAudioURL,
              imageURL: explanationImageURL,
            },
      options: shuffleArray(options),
      contentId: meta.contentId,
      sequence: meta.sequence,
      gameSessionContentId:
        q.contentTypeId === CONTENT_TYPES.TEXT
          ? aGameSessionContentId
          : meta.gameSessionContentId,
      audioURL: qFiles.audioURL,
      imageURL: qFiles.imageURL,
      isPair: q.contentTypeId === CONTENT_TYPES.TEXT,
      exerciseId: meta.exerciseId,
      moduleActivityId: q?.moduleActivityId || 0,
    }
  })
}

const processWordsData = (rawList) => {
  const list = normalizeRawList(rawList)
  if (list.length === 0) return []
  const questions = list.filter(
    (item) => Number(item.contentTypeId) === 1 && item.isPrimaryContent === true
  )
  return questions.map((q, qIndex) => {
    const meta = getQuestionMeta(q)
    const { audioURL } = extractFiles(q)
    const explanationItem = findExplanation(
      list,
      meta.sequence,
      meta.exerciseId
    )
    const explanationText = explanationItem ? explanationItem.text || "" : ""
    const imageURL = explanationItem?.files?.find(
      (f) => f.fileTypeId === FILE_TYPES.IMAGE
    )?.fileURL
    return buildBaseQuestion(q, qIndex, {
      correctSentence: q.text,
      explanation: explanationText,
      audioURL,
      imageURL,
      translatedText: explanationItem?.translatedText || "",
      gameUserFavoriteContentId:
        q.gameUserFavoriteContentId ||
        explanationItem?.gameUserFavoriteContentId ||
        q.metadata?.gameUserFavoriteContentId,
      isFavorite: q.isFavorite || explanationItem?.isFavorite,
    })
  })
}

const MEMORY_PALACE_LIMITED_GAMES = [
  GAMES.MEMORY_PALACE,
  GAMES.WORD_HUNT,
  GAMES.CROSSWORD_QUEST,
  GAMES.WORDMAZE,
]

const selectProcessor = (gameId, contents) => {
  const processor = {
    [GAMES.PHRASE_BUILDER]: processPhraseBuilderData,
    [GAMES.SENTENCE_SCRAMBLE]: processSentenceScrambleData,
    [GAMES.TRANSLATION_DUEL]: processTranslationDuelExercises,
    [GAMES.SCENARIO_SNAP]: processScenarioSnapExercises,
    [GAMES.CONVERSATION_LAB]: processConversationLabExercises,
    [GAMES.FLASHCARD_BLITZ]: processWordsData,
  }[gameId]

  if (processor) return processor(contents)

  if (MEMORY_PALACE_LIMITED_GAMES.includes(gameId)) {
    const processed = processMemoryPalaceData(contents)
    if (processed.length > 0) {
      processed[0].words = processed[0].words.slice(0, 8)
    }
    return processed
  }

  if (gameId === GAMES.WORD_MATCH) return [{ contents }]

  return processFixItRushData(contents)
}

export { selectProcessor, shuffleArray, extractFiles }

export default selectProcessor
